import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MonoTypeOperatorFunction, Observable, catchError, map, of, shareReplay, switchMap, tap } from 'rxjs';
import { API_HOST } from '@onrsr/ui/core';
import { Session } from '../models/session';
import { AppAbility } from './ability';

@Injectable({ providedIn: 'root' })
export class SessionService {
  private readonly http = inject(HttpClient);
  private readonly apiHost = inject(API_HOST);
  private readonly ability = inject(AppAbility);

  private readonly sessionSignal = signal<Session | null>(null);
  private readonly loadedSignal = signal(false);
  private readonly versionSignal = signal(0);
  private session$: Observable<Session | null> | null = null;

  readonly session = this.sessionSignal.asReadonly();
  readonly loaded = this.loadedSignal.asReadonly();
  /** Increments whenever permissions are (re)applied; the shell remounts on it so the menu is rebuilt. */
  readonly version = this.versionSignal.asReadonly();

  load(): Observable<Session | null> {
    this.session$ ??= this.http.get<Session>(`${this.apiHost}/v1.0/me`).pipe(
      catchError(() => of(null)),
      tap((session) => this.apply(session)),
      shareReplay(1)
    );
    return this.session$;
  }

  /** Re-fetches the session after a mutation that may change the caller's permissions. */
  refreshAfter<T>(): MonoTypeOperatorFunction<T> {
    return switchMap((value) => {
      this.session$ = null;
      return this.load().pipe(map(() => value));
    });
  }

  /** Updates office on the active session without remounting the shell (permissions unchanged). */
  patchOffice(office: string): void {
    const current = this.sessionSignal();
    if (!current) {
      return;
    }
    this.sessionSignal.set({ ...current, office });
    this.session$ = null;
  }

  clear(): void {
    this.session$ = null;
    this.ability.update([]);
    this.sessionSignal.set(null);
    this.loadedSignal.set(false);
  }

  private apply(session: Session | null): void {
    this.ability.update(session?.rules ?? []);
    this.sessionSignal.set(session);
    this.loadedSignal.set(true);
    this.versionSignal.update((version) => version + 1);
  }
}
